import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-lg text-xs font-bold uppercase tracking-tighter transition-all focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-[#6bff8f] disabled:pointer-events-none disabled:opacity-50 active:scale-95 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        // Stitch primary: green bg, dark text, glow on hover
        default:     "bg-[#6bff8f] text-[#005f28] hover:shadow-[0_0_15px_rgba(107,255,143,0.3)]",
        destructive: "bg-[#ff7351] text-white hover:bg-[#d53d18]",
        // Stitch secondary: surface-container-highest, no border
        outline:     "bg-[#262626] text-white hover:bg-[#1f1f1f]",
        secondary:   "bg-[#1f1f1f] text-white hover:bg-[#262626]",
        ghost:       "text-[#ababab] hover:bg-[#262626] hover:text-white",
        link:        "text-[#6bff8f] underline-offset-4 hover:underline",
      },
      size: {
        default: "h-10 px-5 py-2",
        sm:      "h-8 px-3 text-[10px]",
        lg:      "h-12 px-8 text-sm",
        icon:    "h-10 w-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return <Comp className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props} />;
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
